/**
 * FilterableSelectPopover Hook
 *
 * Lifts the FilterableLocationsSelect dropdown into the browser's top layer via
 * the HTML Popover API, so the option list is never clipped by an ancestor's
 * `overflow` or `z-index` (e.g. the account modal's scrollable tab row).
 *
 * The component still drives open/close through the `is-collapsed` class on its
 * root (via Phoenix JS commands). This hook mirrors that class state onto
 * showPopover()/hidePopover(), sizes the control to the longest option, and
 * positions the popup under the trigger.
 *
 * Note: a `[popover]` element has a UA style of `inset: 0`, so any time we
 * measure or place it we must reset `inset: auto` first — otherwise it stretches
 * edge-to-edge across the viewport instead of shrinking to its content / our
 * explicit width.
 *
 * Browser support: Chrome 114+, Safari 17+, Firefox 125+. On unsupported
 * browsers it no-ops, leaving the original in-flow dropdown behavior.
 */

const GAP = 4 // px between trigger and popup
const PAD = 8 // px minimum distance from viewport edges
const TRIGGER_EXTRA = 44 // px added to the option width for the trigger's arrow + gap

const FilterableSelectPopover = {
  mounted() {
    this.popup = this.el.querySelector(".checkboxes-dropdown-target")
    this.anchor = this.el.querySelector("[data-popover-anchor]") || this.el

    // Degrade gracefully where the Popover API is unavailable.
    if (!this.popup || typeof this.popup.showPopover !== "function") return

    this.onBeforeToggle = (event) => {
      if (event.newState === "open") this.position()
    }
    this.popup.addEventListener("beforetoggle", this.onBeforeToggle)

    this.observer = new MutationObserver(() => this.sync())
    this.observer.observe(this.el, {
      attributes: true,
      attributeFilter: ["class"],
    })

    // A fluid select can live in a responsive flex item. Re-apply its measured
    // width whenever that containing item changes size so the trigger fills the
    // available row without forcing the row wider than its container.
    if (typeof ResizeObserver === "function" && this.el.parentElement) {
      this.widthObserver = new ResizeObserver(() => {
        this.applyWidth()
        if (this.popup.matches(":popover-open")) this.position()
      })
      this.widthObserver.observe(this.el.parentElement)
    }

    // Size the control to fit the longest option. Measured once here (while the
    // full, unfiltered list is rendered) and re-applied on update, rather than
    // re-measured — so it never jumps as the list filters down.
    this.syncWidth()
    this.sync()
  },

  updated() {
    // morphdom strips the inline styles we set (they aren't in the server DOM),
    // so re-apply the stored width, reconcile open state, and re-position.
    this.applyWidth()
    this.sync()
    if (this.popup && this.popup.matches(":popover-open")) this.position()
  },

  sync() {
    if (!this.popup) return
    const shouldOpen = !this.el.classList.contains("is-collapsed")
    const isOpen = this.popup.matches(":popover-open")
    if (shouldOpen && !isOpen) this.popup.showPopover()
    else if (!shouldOpen && isOpen) this.popup.hidePopover()
  },

  syncWidth() {
    if (!this.popup) return

    const wasOpen = this.popup.matches(":popover-open")
    const prev = {
      width: this.popup.style.width,
      inset: this.popup.style.inset,
      display: this.popup.style.display,
      visibility: this.popup.style.visibility,
      position: this.popup.style.position,
      left: this.popup.style.left,
    }

    // Reset the UA `inset: 0` and let the popup shrink to its content (longest
    // option), measured offscreen while invisible.
    this.popup.style.inset = "auto"
    this.popup.style.width = "auto"
    if (!wasOpen) {
      this.popup.style.display = "block"
      this.popup.style.visibility = "hidden"
      this.popup.style.position = "fixed"
      this.popup.style.left = "-9999px"
    }

    const optionWidth = Math.ceil(this.popup.getBoundingClientRect().width)

    this.popup.style.width = prev.width
    this.popup.style.inset = prev.inset
    this.popup.style.display = prev.display
    this.popup.style.visibility = prev.visibility
    this.popup.style.position = prev.position
    this.popup.style.left = prev.left

    this._syncedWidth = optionWidth + TRIGGER_EXTRA
    this.applyWidth()
  },

  applyWidth() {
    if (!this._syncedWidth) return

    const availableWidth = this.el.parentElement?.getBoundingClientRect().width
    const hasAvailableWidth =
      Number.isFinite(availableWidth) && availableWidth > 0
    const fluid = this.el.hasAttribute("data-fluid-width")
    const width =
      fluid && hasAvailableWidth ? availableWidth : this._syncedWidth

    this.el.style.width = `${Math.min(width, availableWidth || width)}px`
  },

  position() {
    const isOpen = this.popup.matches(":popover-open")
    const anchorWidth = Math.ceil(this.anchor.getBoundingClientRect().width)
    const naturalWidth = Math.max(this._syncedWidth || 0, anchorWidth)
    const width = Math.min(naturalWidth, window.innerWidth - PAD * 2)

    // Reset the UA `inset: 0` and pin an explicit width up front so the height
    // we measure below reflects the real (wrapped) layout.
    this.popup.style.inset = "auto"
    this.popup.style.margin = "0"
    this.popup.style.width = `${width}px`

    // Before it enters the top layer the popover is display:none and can't be
    // measured, so make it measurable-but-invisible just for the calculation.
    if (!isOpen) {
      this.popup.style.display = "block"
      this.popup.style.visibility = "hidden"
    }

    const a = this.anchor.getBoundingClientRect()
    const p = this.popup.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    let top = a.bottom + GAP
    let left = a.left

    if (left + width > vw - PAD) left = vw - width - PAD
    if (left < PAD) left = PAD
    // Flip above the trigger when there's no room below.
    if (top + p.height > vh - PAD) top = a.top - p.height - GAP
    if (top < PAD) top = PAD

    if (!isOpen) {
      this.popup.style.display = ""
      this.popup.style.visibility = ""
    }

    this.popup.style.position = "fixed"
    this.popup.style.top = `${top}px`
    this.popup.style.left = `${left}px`
  },

  destroyed() {
    if (this.observer) this.observer.disconnect()
    if (this.widthObserver) this.widthObserver.disconnect()
    if (this.popup && this.onBeforeToggle) {
      this.popup.removeEventListener("beforetoggle", this.onBeforeToggle)
    }
  },
}

export default FilterableSelectPopover
